// hooks/useSchedule.js
import { useState, useEffect, useCallback } from 'react';
import { visitService } from '../services/visitService';
import schema from '../forms/schema.json';

const { properties } = schema;

// Начальное состояние расписания
const createInitialSchedule = () => {
  return Object.keys(properties).reduce((acc, key) => {
    acc[key] = { 
      active: false, 
      time: { from: '', to: '' } 
    };
    return acc;
  }, {});
};

const HOURS_RANGE = { min: 9, max: 20 };
const ALLOWED_MINUTES = [0, 15, 30, 45];
const MIN_DURATION = 60;
const MAX_FREE_DAYS = 3;

export const useSchedule = (kgId, onClose) => {
  const [schedule, setSchedule] = useState(createInitialSchedule());
  const [initialHash, setInitialHash] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Вычисление хеша для сравнения изменений
  const calculateHash = useCallback((data) => {
    return Object.keys(data).reduce(
      (acc, key) => acc + key + data[key].active + (data[key].time.from || '') + (data[key].time.to || ''),
      ''
    );
  }, []);

  // Загрузка расписания
  const loadSchedule = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await visitService.getSchedule(kgId);
      
      if (response?.data?.days) {
        const newSchedule = createInitialSchedule();
        
        response.data.days.forEach((day) => {
          const key = day.day;
          if (newSchedule[key]) {
            newSchedule[key].active = true;
            if (day.hours?.[0]) {
              newSchedule[key].time.from = day.hours[0].from || '';
              newSchedule[key].time.to = day.hours[0].to || '';
            }
          }
        });

        setSchedule(newSchedule);
        setInitialHash(calculateHash(newSchedule));
      }
    } catch (err) {
      setError(err.message || 'Помилка завантаження розкладу');
      console.error('Error loading schedule:', err);
    } finally {
      setLoading(false);
    }
  }, [kgId, calculateHash]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  // Валидация времени
  const validateTime = (from, to) => {
    if (!from || !to) return false;
    if (from.length < 4 || from.length > 5 || to.length < 4 || to.length > 5) return false;

    const [fromHours, fromMinutes] = from.split('-').map(Number);
    const [toHours, toMinutes] = to.split('-').map(Number);

    if (isNaN(fromHours) || isNaN(fromMinutes) || isNaN(toHours) || isNaN(toMinutes)) return false;
    if (fromHours < HOURS_RANGE.min || fromHours >= HOURS_RANGE.max) return false;
    if (toHours < HOURS_RANGE.min || toHours > HOURS_RANGE.max) return false;
    if (!ALLOWED_MINUTES.includes(fromMinutes) || !ALLOWED_MINUTES.includes(toMinutes)) return false;

    const duration = (toHours * 60 + toMinutes) - (fromHours * 60 + fromMinutes);
    return duration >= MIN_DURATION;
  };

  // Валидация всего расписания
  const validateSchedule = useCallback(() => {
    let activeCount = 0;
    
    for (const key of Object.keys(schedule)) {
      if (!schedule[key].active) continue;
      
      activeCount++;
      const { from, to } = schedule[key].time;
      
      if (!validateTime(from, to)) {
        setError(`Невірно зазначений час для ${properties[key].title}`);
        return false;
      }
    }

    if (activeCount > MAX_FREE_DAYS) {
      setError(`Можна вибрати не більше ${MAX_FREE_DAYS} днів`);
      return false;
    }

    return true;
  }, [schedule]);

  // Сохранение расписания
  const saveSchedule = async () => {
    if (calculateHash(schedule) === initialHash) {
      setError('Не зроблено жодних змін до розкладу');
      return false;
    }

    if (!validateSchedule()) return false;

    const transformedData = {
      days: Object.entries(schedule)
        .filter(([_, value]) => value.active)
        .map(([key, value]) => ({
          day: key,
          hours: [value.time],
          time_interval: 30
        }))
    };

    setLoading(true);
    setError(null);
    
    try {
      await visitService.saveSchedule(kgId, transformedData);
      alert('Дані успішно зафіксовані');
      if (onClose) onClose();
      return true;
    } catch (err) {
      setError(err.message || 'Помилка збереження розкладу');
      console.error('Error saving schedule:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Обновление активности дня
  const updateActive = (key, value) => {
    setSchedule(prev => ({
      ...prev,
      [key]: { 
        ...prev[key], 
        active: value 
      }
    }));
  };

  // Обновление времени
  const updateTime = (key, part, value) => {
    // Валидация ввода (только цифры и дефис)
    const match = value.match(/^[0-9-]{0,5}$/);
    if (value.length > 0 && !match) return;

    setSchedule(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        time: { 
          ...prev[key].time, 
          [part]: value 
        }
      }
    }));
  };

  return {
    schedule,
    loading,
    error,
    updateActive,
    updateTime,
    saveSchedule,
    clearError: () => setError(null)
  };
};